import { AVATAR_LIMITS } from "@uzverse/contracts";
import type { AvatarSpec } from "@uzverse/contracts";

export type { AvatarSpec };
export type Body = AvatarSpec["body"];

/**
 * What a character can look like. The bundled models (`public/world/models/characters/`) are a
 * male and a female realistic human; a spec picks skin tone, hairstyle and color, and an outfit
 * with a color. Every field is an index into the palettes below, so the saved spec is tiny, can
 * be validated by range, and survives a palette growing later.
 */

/** Skin tones (sRGB), light to deep. The model's texture is a medium tan; the tint scales it. */
export const SKIN_TONES = [
  "#efcfb4",
  "#e3b896",
  "#d2a07a",
  "#bb8662",
  "#a06c48",
  "#845236",
  "#5f3824",
  "#40251a",
] as const;
/** Mean sRGB color of the model's own skin texture (measured), the tint's reference. */
const SKIN_BASE = [168, 117, 81] as const;

export const HAIR_COLORS = [
  "#1b1512",
  "#3a2618",
  "#5b3a22",
  "#7b4a26",
  "#a0522d",
  "#c9a15a",
  "#9a9a9a",
  "#b8472a",
] as const;

export const OUTFIT_COLORS = [
  "#2f4a8a",
  "#e9e6df",
  "#b4343f",
  "#2a8c82",
  "#d9a22a",
  "#6f4fb0",
  "#4c8a45",
  "#24262c",
  "#7d838c",
  "#d9708f",
] as const;

export interface HairStyle {
  id: string;
  /** Nodes of the character file shown for this style. */
  nodes: readonly string[];
  bodies: readonly Body[];
}
/** Hairstyle slots (the spec's `hair` index). */
export const HAIR_STYLES: readonly HairStyle[] = [
  { id: "buzzed", nodes: ["Hair_Buzzed"], bodies: ["male", "female"] },
  { id: "parted", nodes: ["Hair_SimpleParted"], bodies: ["male", "female"] },
  { id: "long", nodes: ["Hair_Long"], bodies: ["male", "female"] },
  { id: "buns", nodes: ["Hair_Buns"], bodies: ["male", "female"] },
  { id: "short-f", nodes: ["Hair_BuzzedFemale"], bodies: ["male", "female"] },
  { id: "beard", nodes: ["Hair_Buzzed", "Hair_Beard"], bodies: ["male"] },
];

export interface Outfit {
  id: string;
  top: "Top_short" | "Top_long" | "Top_jacket";
  bottom: "Bottom_long" | "Bottom_shorts";
  /** Trouser / short color (sRGB). */
  bottomColor: string;
}
export const OUTFITS: readonly Outfit[] = [
  { id: "tee", top: "Top_short", bottom: "Bottom_long", bottomColor: "#6b5f4c" },
  { id: "shirt", top: "Top_long", bottom: "Bottom_long", bottomColor: "#2e3139" },
  { id: "blazer", top: "Top_jacket", bottom: "Bottom_long", bottomColor: "#23252b" },
  { id: "shorts", top: "Top_short", bottom: "Bottom_shorts", bottomColor: "#4c6a8f" },
  { id: "hoodie", top: "Top_jacket", bottom: "Bottom_long", bottomColor: "#3d5675" },
];

export const SPEC_LIMITS = AVATAR_LIMITS;

const clampInt = (n: unknown, max: number): number => {
  const v = typeof n === "number" && Number.isFinite(n) ? Math.trunc(n) : 0;
  return Math.max(0, Math.min(max - 1, v));
};

/** Keeps every index inside its palette and the hairstyle valid for the body. */
export function normalizeSpec(spec: Partial<AvatarSpec>): AvatarSpec {
  const body: Body = spec.body === "female" ? "female" : "male";
  let hair = clampInt(spec.hair, HAIR_STYLES.length);
  if (!HAIR_STYLES[hair]!.bodies.includes(body)) hair = 0;
  return {
    body,
    skin: clampInt(spec.skin, SKIN_TONES.length),
    hair,
    hairColor: clampInt(spec.hairColor, HAIR_COLORS.length),
    outfit: clampInt(spec.outfit, OUTFITS.length),
    outfitColor: clampInt(spec.outfitColor, OUTFIT_COLORS.length),
  };
}

/** The hairstyles a body may wear, as spec indices. */
export function hairOptions(body: Body): number[] {
  return HAIR_STYLES.flatMap((h, i) => (h.bodies.includes(body) ? [i] : []));
}

export function hashOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** A deterministic, varied character for anyone without a saved one (agents, the assistant). */
export function specFor(seed: string): AvatarSpec {
  const h = hashOf(seed);
  const pick = (shift: number, n: number) => ((h >>> shift) ^ (h >>> (shift + 11))) % n;
  const body: Body = pick(0, 2) === 0 ? "male" : "female";
  const opts = hairOptions(body);
  return normalizeSpec({
    body,
    skin: pick(3, SKIN_TONES.length),
    hair: opts[pick(7, opts.length)]!,
    hairColor: pick(13, HAIR_COLORS.length),
    outfit: pick(17, OUTFITS.length),
    outfitColor: pick(23, OUTFIT_COLORS.length),
  });
}

// ---------------------------------------------------------------- the assistant's `appearance`

const APPEARANCE_PREFIX = "a1:";

/** `appearance` string for the assistant setting: one digit per field, ≤ 60 chars. */
export function encodeAppearance(spec: AvatarSpec): string {
  const s = normalizeSpec(spec);
  return `${APPEARANCE_PREFIX}${s.body === "female" ? "f" : "m"}${s.skin}${s.hair}${s.hairColor}${s.outfit}${s.outfitColor}`;
}

/** Reads an `appearance` string; "default" and anything unknown map to a character by `seed`. */
export function decodeAppearance(appearance: string | undefined, seed: string): AvatarSpec {
  const m = /^a1:([mf])(\d)(\d)(\d)(\d)(\d)$/.exec(appearance ?? "");
  if (!m) return specFor(seed);
  return normalizeSpec({
    body: m[1] === "f" ? "female" : "male",
    skin: Number(m[2]),
    hair: Number(m[3]),
    hairColor: Number(m[4]),
    outfit: Number(m[5]),
    outfitColor: Number(m[6]),
  });
}

// ---------------------------------------------------------------- colors for the renderer

const lin = (c: number): number => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};
export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Linear multiplier for the skin material's color: lands the model's tan on the chosen tone. */
export function skinTint(index: number): [number, number, number] {
  const t = hexToRgb(SKIN_TONES[clampInt(index, SKIN_TONES.length)]!);
  return [
    lin(t[0]) / lin(SKIN_BASE[0]),
    lin(t[1]) / lin(SKIN_BASE[1]),
    lin(t[2]) / lin(SKIN_BASE[2]),
  ];
}

/** Hair texture is a pale grey (mean about 0.7), so the tint is lifted to land on the color. */
export const HAIR_GAIN = 1.45;

// ---------------------------------------------------------------- agents in the floors

/** Outfit colors that read as a department: stable hex per department name, or the brand's own. */
const DEPARTMENT_COLORS = [
  "#2f4a8a",
  "#2a8c82",
  "#b4343f",
  "#d9a22a",
  "#6f4fb0",
  "#4c8a45",
  "#3d6fa0",
];

export interface AgentLook {
  spec: AvatarSpec;
  /** Overrides the top's color (a company brand color or a department color), sRGB hex. */
  topColor: string;
}

/** The look of an agent: a deterministic character per id, the top tinted by brand or department. */
export function agentLook(
  agentId: string,
  opts: { brand?: string | undefined; department?: string | undefined } = {},
): AgentLook {
  const spec = specFor(agentId);
  const color =
    opts.brand && /^#[0-9a-f]{6}$/i.test(opts.brand)
      ? opts.brand
      : opts.department
        ? DEPARTMENT_COLORS[hashOf(opts.department) % DEPARTMENT_COLORS.length]!
        : OUTFIT_COLORS[spec.outfitColor]!;
  return { spec, topColor: color };
}
